import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Paper, Grid, Box, CircularProgress, Stack } from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTES } from '../../../../constants/routes';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomSelect } from '../../../../components/CustomSelect';
import { createTemplate, getTemplateById, getTimeBounds, updateTemplate } from '../../../../store/mailing/actions';
import { CustomInput } from '../../../../components/CustomInput';
import { CATEGORIES, PRIORITIES, TYPES } from '../../constants';
import * as yup from 'yup';
import { CustomInputTextArea } from '../../../../components/CustomInputTextArea';
import { CustomRadioGroup } from '../../../../components/CustomRadioGroup';
import { CustomModal } from '../../../../components/CustomModal';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { NotFoundItems } from '../../../../components/NotFoundItems';
import { CustomLoadingButton } from '../../../../components/CustomLoadingButton';

const validationSchema = yup.object({
  name: yup.string().required('Обязательное поле'),
  text: yup.string().required('Обязательное поле').max(1000, 'Максимум 1000 символов'),
  channel_type_code: yup.string().required('Обязательное поле'),
  topic: yup.string().when('mailing_type', {
    is: val => val === 'email',
    then: yup.string().required('Обязательное поле'),
  }),
  mailing_type: yup.string().required('Обязательное поле'),
  time_bound: yup.object().required('Обязательное поле'),
  campaign_type: yup.object().required('Обязательное поле'),
  priority: yup.object().required('Обязательное поле'),
});

const mainStyleInput = {
  variant: 'outlined',
  size: 'small'
};

export const AddTemplate = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  const isEdit = id !== ':id';

  const { timeBounds, template, fetching, createUpdateError } = useSelector(state => state.mailing);

  const [isCancelModal, setIsCancelModal] = useState(false);
  const [type, setType] = useState(TYPES[0].value);
  const [emailError, setEmailError] = useState(false);
  const [customTouched, setCustomTouched] = useState({});
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const messagePlaceholders = {
    value: 'Чтобы настраивать персонализированные рассылки необходимо в текст сообщения шаблона добавить переменные:',
    items: [
      { start: 'Name', end: 'Имя абонента' },
      { start: 'Surname', end: 'Фамилия абонента' },
      { start: 'BonusCount', end: 'Сумма бонусов' },
      { start: 'ExpireDate', end: 'Срок действия бонусов' },
      { start: 'CampaignName', end: 'Наименование кампании.' },
    ],
  };

  useEffect(() => {
    if (isEdit) dispatch(getTemplateById(id));
    dispatch(getTimeBounds());
  }, // eslint-disable-next-line
    [dispatch, id]);

  useEffect(() => {
    if (template.mailing_type) setType(template.mailing_type);
  }, [template]);

  const registerInput = (name) => ({
    name,
    value: values[name],
    error: typeof values[name] === 'string' && errors[name],

    ...mainStyleInput
  });
  const pageTitle = isEdit ? 'Редактировать рассылку' : 'Создать рассылку';
  const redirectToMailing = () => history.push(ROUTES.MAILING);

  const initialValues = {
    mailing_type: isEdit ? template.mailing_type : TYPES[0].value,
    name: isEdit ? template.name : null,
    topic: isEdit ? template.topic || '' : '',
    text: isEdit ? template.text : null,
    channel_type_code: isEdit ? template.channel_type_code : null,
    time_bound: isEdit && timeBounds?.find(bound => bound.slug === template.time_bound) || null,
    campaign_type: isEdit ? CATEGORIES.find(category => category.value === template.campaign_type) : null,
    priority: isEdit ? PRIORITIES.find(priority => priority.value === template.priority) : null,
  }

  const { handleSubmit, values, handleChange, handleBlur, setFieldValue, errors, dirty, isValid } = useFormik({
    initialValues,
    validateOnChange: true,
    validateOnBlur: false,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const newValues = {
        mailing_type: values.mailing_type,
        name: values.name,
        topic: values.mailing_type === 'email' ? values.topic || '' : '',
        text: values.text,
        channel_type_code: values.channel_type_code,
        time_bound: values.time_bound.slug,
        campaign_type: values.campaign_type.value,
        priority: values.priority.value,
      }

      isEdit ?
        dispatch(updateTemplate(id, newValues, redirectToMailing)) :
        dispatch(createTemplate(newValues, redirectToMailing));
    }
  });

  const touchedById = (name) => isEdit && setCustomTouched({ [name]: true, ...customTouched });

  const isAllowedToCreateMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.CreateMailingTemplates.value);
  const isAllowedToUpdateMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.UpdateMailingTemplates.value);

  return (
    <>
      {fetching ? (
        <Box
          sx={{
            marginTop: '150px',
            textAlign: 'center'
          }}>
          <CircularProgress data-testid="CircularProgress" />
        </Box>
      ) : (
        <>
          <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1" data-testid="Title">
            {pageTitle}
          </Typography>
          <Breadcrumbs mb="24px" aria-label="breadcrumb" data-testid="Breadcrumbs">
            <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.MAILING}>
              CMC/Email рассылка
            </Link>
            <Typography color="text.secondary">{pageTitle}</Typography>
          </Breadcrumbs>

          {
            isEdit || isAllowedToCreateMailingTemplates ? (
              <Paper
                sx={{
                  padding: '24px'
                }}>
                <form onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', gap: '18px' }}>
                    <Typography variant="subtitleForm">Тип рассылки:</Typography>
                    <CustomRadioGroup
                      {...registerInput('mailing_type')}
                      options={TYPES}
                      setFieldValue={setFieldValue}
                      value={type}
                      onChange={e => setType(e)}
                      disabled={isEdit}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '34px', mt: '24px' }}>
                    <Box sx={{ display: 'grid', rowGap: '24px' }}>
                      <Grid container spacing={2}>
                        <Grid item sx={{ width: '100%' }}>
                          <CustomInput
                            {...registerInput('name')}
                            placeholder="Название шаблона"
                            onChange={(e) => {
                              touchedById('name');
                              return handleChange(e);
                            }}
                            onBlur={handleBlur}
                            sx={{ width: '100%' }}
                          />
                        </Grid>
                        {type === 'email' && (<Grid item sx={{ width: '100%' }}>
                          <CustomInput
                            {...registerInput('topic')}
                            placeholder="Заголовок сообщения"
                            onChange={(e) => {
                              touchedById('topic');
                              return handleChange(e);
                            }}
                            onKeyDown={() => setEmailError(true)}
                            onBlur={handleBlur}
                            sx={{ width: '100%' }}
                            error={emailError && errors['topic']}
                          />
                        </Grid>)}
                        <Grid item sx={{ width: '100%' }}>
                          <CustomInputTextArea
                            {...registerInput('text')}
                            onChange={(e) => {
                              touchedById('text');
                              return handleChange(e);
                            }}
                            style={{ height: 120, width: '100%' }}
                            placeholder="Текст сообщения"
                            onPaste={(e) => {if (e.clipboardData.getData('text').length > 1000) e.preventDefault();}}
                          />
                          <Typography color={errors['text'] && values['text'] ? "error.main" : "text.secondary"}>До 1000 символов.</Typography>
                          <Stack
                            direction='column'
                            spacing='0'
                            bgcolor='#F3F9FF'
                            sx={{ mt: 1, padding: '12px 16px', fontSize: '16px', lineHeight: '20px', color: '#151C22', borderRadius: '6px' }}
                          >
                            <Box>{ messagePlaceholders.value }</Box>
                            {
                              messagePlaceholders.items.map((item, index) => {
                                return (
                                  <Box key={index}>
                                    <Typography fontWeight='600' display='inline'>&#x7B;&#x7B;{ item.start }&#x7D;&#x7D;</Typography> - { item.end }
                                  </Box>
                                );
                              })
                            }
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ display: 'grid', rowGap: '24px' }}>
                      <Grid container spacing={2}>
                        <Grid item sx={{ width: '100%' }}>
                          <CustomInput
                            {...registerInput('channel_type_code')}
                            placeholder="Код типа коммуникации"
                            onChange={(e) => {
                              touchedById('channel_type_code');
                              return handleChange(e);
                            }}
                            onBlur={handleBlur}
                            sx={{ width: '100%' }}
                          />
                        </Grid>
                        <Grid item sx={{ width: '100%' }}>
                          <CustomSelect
                            {...registerInput('time_bound')}
                            placeholder="Период отправки"
                            onChange={() => {
                              touchedById('time_bound');
                              return handleChange;
                            }}
                            onBlur={handleBlur}
                            sx={{ width: '100%' }}
                            options={timeBounds}
                            setFieldValue={setFieldValue}
                            disabled={fetching}
                          />
                        </Grid>
                        <Grid item sx={{ width: '100%' }}>
                          <CustomSelect
                            {...registerInput('campaign_type')}
                            placeholder="Категория кампании"
                            onChange={() => {
                              touchedById('campaign_type');
                              return handleChange;
                            }}
                            onBlur={handleBlur}
                            sx={{ width: '100%' }}
                            options={CATEGORIES}
                            setFieldValue={setFieldValue}
                          />
                        </Grid>
                        <Grid item sx={{ width: '100%' }}>
                          <CustomSelect
                            {...registerInput('priority')}
                            placeholder="Приоритет отправки"
                            onChange={() => {
                              touchedById('priority');
                              return handleChange;
                            }}
                            onBlur={handleBlur}
                            sx={{ width: '100%' }}
                            options={PRIORITIES}
                            setFieldValue={setFieldValue}
                          />
                        </Grid>
                        <Grid item sx={{ width: '100%', height: '100%' }}/>
                      </Grid>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '36px' }}>
                    <Box sx={{ color: 'error.main', fontSize: '1rem' }} data-testid="ErrorMessage">{''}</Box>
                    <Box>
                      <CustomButton
                        onClick={() => {
                          if (JSON.stringify(initialValues) === JSON.stringify(values)) history.push(ROUTES.MAILING);
                          else setIsCancelModal(true);
                        }}
                        color="secondary"
                        variant="contained"
                        sx={{ marginRight: '16px' }}
                      >
                        Отмена
                      </CustomButton>
                      <CustomLoadingButton
                        type="submit"
                        variant="contained"
                        loading={fetching}
                        disabled={
                          isEdit ?
                            !Object.values(customTouched).length || !isValid || !dirty || !isAllowedToUpdateMailingTemplates :
                            !isValid || !dirty || !isAllowedToCreateMailingTemplates
                        }
                        tooltip={
                          (isEdit && !isAllowedToUpdateMailingTemplates) ||
                          (!isEdit && !isAllowedToCreateMailingTemplates) ?
                            DISABLED_CONTROL_TOOLTIP :
                            null
                        }
                      >
                        Сохранить
                      </CustomLoadingButton>
                    </Box>
                  </Box>
                </form>

                {
                  createUpdateError && (
                    <Box color="error.main" fontSize="1rem" textAlign="center" data-testid="ErrorBlock">
                      {createUpdateError}
                    </Box>
                  )
                }
              </Paper>
            ) : (
              <NotFoundItems message="Вам запрещён доступ к просмотру списка событий"/>
            )
          }

          <CustomModal
            opened={isCancelModal}
            modalClose={() => setIsCancelModal(false)}
            modalSubmit={() => history.push(ROUTES.MAILING)}
            text="Вы уверены, что хотите выйти, не сохранив данные?"
            cancelBtnName="Отмена"
            submitBtnName="Да"
          />
        </>
      )}
    </>
  );
};

